import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Reveal } from "@/components/motion/Reveal";
import { Mail, ArrowUpRight } from "lucide-react";

/**
 * Real send-pipeline activity (last 24h), pulled from the DB. No setInterval,
 * no fake names. Mirrors RealProof: honest empty-state when there's nothing
 * to show.
 */
export function RealExecution() {
  const { data, isLoading } = useQuery({
    queryKey: ["public-execution-24h"],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const [sent, opened, replies, booked, totalSent] = await Promise.all([
        supabase
          .from("sdr_emails")
          .select("id", { count: "exact", head: true })
          .eq("direction", "outbound")
          .gte("sent_at", since),
        supabase
          .from("sdr_emails")
          .select("id", { count: "exact", head: true })
          .eq("direction", "outbound")
          .gte("opened_at", since),
        supabase
          .from("sdr_emails")
          .select("id", { count: "exact", head: true })
          .eq("direction", "inbound")
          .gte("created_at", since),
        supabase
          .from("bookings")
          .select("id", { count: "exact", head: true })
          .gte("created_at", since),
        supabase
          .from("sdr_emails")
          .select("id", { count: "exact", head: true })
          .eq("direction", "outbound"),
      ]);

      return {
        sent24h: sent.count ?? 0,
        opened24h: opened.count ?? 0,
        replies24h: replies.count ?? 0,
        booked24h: booked.count ?? 0,
        totalSent: totalSent.count ?? 0,
      };
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const hasActivity = (data?.sent24h ?? 0) > 0;

  return (
    <section className="relative">
      <div className="max-w-6xl mx-auto px-4 py-20 md:py-28">
        <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto mb-10">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">
            Execution engine
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
            Every send, paced and tracked.
          </h2>
          <p className="text-sm md:text-base text-foreground/85">
            Email-only. Throttled per inbox. Auto-paused on reply. Numbers below are pulled live from our database — refresh to confirm.
          </p>
        </Reveal>

        <div className="rounded-2xl border border-border/60 bg-background/70 backdrop-blur-xl p-4 md:p-8">
          <div className="flex items-center justify-between mb-5 px-1 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-foreground/85">
              <Mail className="h-3 w-3 text-primary" />
              Email channel · last 24h
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/85 tabular-nums">
              {(data?.totalSent ?? 0).toLocaleString()} sent all-time
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Sent" value={data?.sent24h ?? 0} loading={isLoading} accent />
            <Tile label="Opened" value={data?.opened24h ?? 0} loading={isLoading} />
            <Tile label="Replied" value={data?.replies24h ?? 0} loading={isLoading} />
            <Tile label="Booked" value={data?.booked24h ?? 0} loading={isLoading} accent />
          </div>

          {!hasActivity && !isLoading && (
            <div className="mt-6 rounded-xl border border-border/40 bg-foreground/[0.02] p-5 text-center">
              <p className="text-sm text-foreground/85">
                Quiet window right now. We don&apos;t fake activity — when sends happen, they show up here.
              </p>
              <p className="text-[11px] font-mono uppercase tracking-widest text-foreground/55 mt-2">
                {(data?.totalSent ?? 0).toLocaleString()} outbound emails delivered all-time
              </p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-foreground/85">
            Coordinated · throttled · auto-paused on reply
            <ArrowUpRight className="h-3 w-3 text-primary" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Tile({
  label, value, loading, accent,
}: {
  label: string;
  value: number;
  loading?: boolean;
  accent?: boolean;
}) {
  return (
    <div className={`rounded-xl border ${accent ? "border-primary/40 bg-primary/5" : "border-border/50 bg-card/40"} px-4 py-5 text-center`}>
      <div className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">
        {loading ? "—" : value.toLocaleString()}
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/65 mt-1">
        {label}
      </div>
    </div>
  );
}
